import { useCallback, useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { subscribe, unsubscribe } from 'pubsub-js';

import {
  clearConversation,
  sendMessage,
  CHATBOT_HISTORY,
  CHAT_BOT,
  updateDatabaseThenSendInitialMessages,
} from 'genesys/genesys';
import { MessagingServiceEvent } from 'genesys/messaging-service-plugin';
import { PubSubEvent } from 'genesys/pub-sub-events';
import {
  hasAgentConversationStartedState,
  useHasAgentConversationStarted,
} from 'state/hasAgentConversationStarted';
import {
  useConversation,
  useInboundConversationState,
  useOutboundConversationState,
} from 'state/conversation';
import {
  useHasChatWithAgentBtnClicked,
  useSetHasChatWithAgentBtnClicked,
} from 'state/hasChatWithAgentBtnClicked';
import { useSetCoBrowseActioned } from 'state/coBrowseActioned';
import { useSafeLabel } from 'hooks';

import {
  getContentClaims,
  getEventClaims,
  getRestoreQuickReplyClaim,
} from './genesysClaimsUtil';

import messages from './messages';

const AGENT_OUTBOUND = 'outbound';
const AGENT_INBOUND = 'inbound';
const INITIAL_MESSAGE = 'INITIAL_MESSAGE';
const AGENT_BOT_NAME = 'HSBC';

export const useGenesysSubscribeEffect = () => {
  const safelabel = useSafeLabel();

  const setHasAgentConversationStarted = useSetRecoilState(
    hasAgentConversationStartedState,
  );
  const conversation = useConversation();
  const setCoBrowseActioned = useSetCoBrowseActioned();
  const { setInboundConversation } = useInboundConversationState();
  const { setOutboundConversation } = useOutboundConversationState();
  const setHasChatWithAgentBtnClicked = useSetHasChatWithAgentBtnClicked();
  const hasChatWithAgentBtnClicked = useHasChatWithAgentBtnClicked();
  const hasAgentConversationStarted = useHasAgentConversationStarted();

  const handleConversationSuccess = useCallback(
    (topic, data) => {
      const chatHistory = [
        {
          question: INITIAL_MESSAGE,
          by: CHAT_BOT,
        },
        ...conversation.slice(Math.max(conversation.length - 5, 0)),
      ];

      if (!hasChatWithAgentBtnClicked) {
        return;
      }

      setHasAgentConversationStarted(true);

      if (data?.data?.newSession) {
        setHasChatWithAgentBtnClicked(false);
        updateDatabaseThenSendInitialMessages(
          { chatHistoryLength: chatHistory.length, hasChatWithAgentBtnClicked },
          () => {
            sendMessage({ chatHistory });
          },
        );
      }
    },
    [
      setHasAgentConversationStarted,
      conversation,
      hasChatWithAgentBtnClicked,
      setHasChatWithAgentBtnClicked,
    ],
  );

  const handleConversationFailure = useCallback((error) => {
    clearConversation();
  }, []);

  const handleConversationClear = useCallback(() => {
    setHasAgentConversationStarted(false);

    if (hasAgentConversationStarted) {
      setInboundConversation({
        displayName: AGENT_BOT_NAME,
        message: safelabel(messages.genesysEndChatMessage),
        datetime: Date.now(),
      });
    }
  }, [
    setHasAgentConversationStarted,
    setInboundConversation,
    safelabel,
    hasAgentConversationStarted,
  ]);

  const handleMessageRecieved = useCallback(
    (m, { messages }) => {
      const [currentMessage] = messages;
      const { messageType, direction, text } = currentMessage;

      if (
        direction?.toLowerCase() === AGENT_INBOUND &&
        !text?.includes(CHATBOT_HISTORY)
      ) {
        setOutboundConversation(text);
      } else if (
        messageType?.toLowerCase() === AGENT_OUTBOUND ||
        direction?.toLowerCase() === AGENT_OUTBOUND
      ) {
        const {
          id,
          channel,
          text,
          events = [],
          timestamp,
          from,
          content,
          quickReplies,
          originatingEntity,
        } = currentMessage;
        const [event] = events;
        const { message, eventClaims } =
          getEventClaims(event, setCoBrowseActioned, safelabel) ?? {};
        const displayName = channel?.from ?? from;
        const contentClaims = getContentClaims(content) ?? {};
        const restoreQuickReplyClaim =
          getRestoreQuickReplyClaim(quickReplies) ?? {};

        setInboundConversation({
          id,
          displayName: displayName?.nickname ?? 'HSBC',
          message: message ?? text,
          datetime: channel?.time || timestamp,
          additionalClaims: {
            ...eventClaims,
            ...contentClaims,
            ...restoreQuickReplyClaim,
            isAgentFeedback:
              !displayName?.nickname && originatingEntity === 'Bot' && !event,
          },
        });
      }
    },
    [
      safelabel,
      setOutboundConversation,
      setInboundConversation,
      setCoBrowseActioned,
    ],
  );

  const handleMessageRestored = useCallback(
    (m, restoredMessages = []) => {
      restoredMessages.forEach((message) => {
        const { messageType, text, timestamp } = message;

        if (messageType?.toLowerCase() === AGENT_OUTBOUND) {
          handleMessageRecieved(m, { messages: [message] });
        } else if (!text?.includes(CHATBOT_HISTORY)) {
          setOutboundConversation(text, timestamp);
        }
      });
    },
    [handleMessageRecieved, setOutboundConversation],
  );

  const handleConversationRestore = useCallback(() => {
    setHasAgentConversationStarted(true);
  }, [setHasAgentConversationStarted]);

  useEffect(() => {
    subscribe(MessagingServiceEvent.MessagesReceived, handleMessageRecieved);

    return () => {
      unsubscribe(handleMessageRecieved);
    };
  }, [handleMessageRecieved]);

  useEffect(() => {
    subscribe(MessagingServiceEvent.Restored, handleConversationRestore);
    subscribe(PubSubEvent.MessageRestored, handleMessageRestored);

    return () => {
      unsubscribe(handleConversationRestore);
      unsubscribe(handleMessageRestored);
    };
  }, [handleMessageRestored, handleConversationRestore]);

  useEffect(() => {
    subscribe(MessagingServiceEvent.Started, handleConversationSuccess);
    subscribe(MessagingServiceEvent.Error, handleConversationFailure);
    subscribe(
      MessagingServiceEvent.ConversationCleared,
      handleConversationClear,
    );

    return () => {
      unsubscribe(handleConversationClear);
      unsubscribe(handleConversationFailure);
      unsubscribe(handleConversationSuccess);
    };
  }, [
    handleConversationSuccess,
    handleConversationClear,
    handleConversationFailure,
  ]);
};
